import { create } from 'zustand'
import { Room } from 'colyseus.js'
import { networkClient } from '../network/client'

export type Screen = 'menu' | 'lobby' | 'waiting' | 'game'
export type PlayerRole = 'flux' | 'logic' | 'spark' | 'echo'

export interface Player {
  sessionId: string
  nickname: string
  role: PlayerRole
  x: number
  y: number
  score: number
  isReady: boolean
  isAI: boolean
}

export interface RoomInfo {
  roomId: string
  name: string
  players: number
  maxPlayers: number
}

export interface ActivePuzzle {
  puzzleId: string
  type: string
  difficulty: string
  sequence: string[]
}

interface GameState {
  // UI State
  screen: Screen
  nickname: string
  selectedRole: PlayerRole
  error: string | null

  // Room state
  currentRoomId: string | null
  isHost: boolean
  players: Record<string, Player>
  mySessionId: string | null

  // Game state
  phase: 'lobby' | 'explore' | 'puzzle' | 'cutscene'
  timer: number
  stability: number
  squad: 'solo' | 'duo' | 'trio' | 'full'

  // Available rooms
  availableRooms: RoomInfo[]

  // Puzzle state
  activePuzzle: ActivePuzzle | null

  // Actions
  setScreen: (screen: Screen) => void
  setNickname: (nickname: string) => void
  setSelectedRole: (role: PlayerRole) => void
  setError: (error: string | null) => void

  // Network actions
  joinLobby: () => Promise<void>
  createRoom: (roomName: string) => Promise<void>
  joinRoom: (roomId: string) => Promise<void>
  quickJoin: () => Promise<void>
  leaveRoom: () => void
  toggleReady: () => void
  startGame: () => void
  changeRole: (role: PlayerRole) => void
  refreshRooms: () => Promise<void>

  // Puzzle actions
  requestPuzzle: (difficulty?: string) => void
  closePuzzle: () => void

  // State sync
  syncFromRoom: (room: Room) => void
}

export const useGameStore = create<GameState>((set, get) => ({
  // Initial UI state
  screen: 'menu',
  nickname: '',
  selectedRole: 'flux',
  error: null,

  // Initial room state
  currentRoomId: null,
  isHost: false,
  players: {},
  mySessionId: null,

  // Initial game state
  phase: 'lobby',
  timer: 1800,
  stability: 100,
  squad: 'solo',

  // Available rooms
  availableRooms: [],

  // Puzzle state
  activePuzzle: null,

  // Basic setters
  setScreen: (screen) => set({ screen }),
  setNickname: (nickname) => set({ nickname }),
  setSelectedRole: (role) => set({ selectedRole: role }),
  setError: (error) => set({ error }),

  // Network actions
  joinLobby: async () => {
    try {
      const { nickname } = get()
      const room = await networkClient.joinLobby(nickname || 'Gracz')

      room.state.rooms.onAdd((roomInfo: any) => {
        const rooms = get().availableRooms
        set({
          availableRooms: [...rooms, {
            roomId: roomInfo.roomId,
            name: roomInfo.name,
            players: roomInfo.players,
            maxPlayers: roomInfo.maxPlayers,
          }]
        })
      })

      room.state.rooms.onRemove((roomInfo: any, _key: string) => {
        const rooms = get().availableRooms.filter(r => r.roomId !== roomInfo.roomId)
        set({ availableRooms: rooms })
      })

      room.state.rooms.onChange((roomInfo: any) => {
        const rooms = get().availableRooms.map(r =>
          r.roomId === roomInfo.roomId
            ? { ...r, players: roomInfo.players, name: roomInfo.name }
            : r
        )
        set({ availableRooms: rooms })
      })

      set({ screen: 'lobby', error: null })
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie polaczyc' })
    }
  },

  createRoom: async (roomName) => {
    try {
      const { nickname, selectedRole } = get()
      await networkClient.createRoom(roomName, nickname, selectedRole)

      const room = networkClient.getGameRoom()
      if (room) {
        get().syncFromRoom(room)
        set({ screen: 'waiting', isHost: true, error: null })
      }
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie stworzyc pokoju' })
    }
  },

  joinRoom: async (roomId) => {
    try {
      const { nickname, selectedRole } = get()
      const room = await networkClient.joinGameRoom(roomId, nickname, selectedRole)
      get().syncFromRoom(room)
      set({ screen: 'waiting', isHost: false, error: null })
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie dolaczyc do pokoju' })
    }
  },

  quickJoin: async () => {
    try {
      const { nickname, selectedRole } = get()
      const room = await networkClient.quickJoin(nickname, selectedRole)
      get().syncFromRoom(room)
      set({ screen: 'waiting', isHost: true, error: null })
    } catch (error: any) {
      set({ error: error.message || 'Nie udalo sie dolaczyc' })
    }
  },

  leaveRoom: () => {
    networkClient.leaveGame()
    set({
      screen: 'menu',
      currentRoomId: null,
      players: {},
      mySessionId: null,
      isHost: false,
      phase: 'lobby',
    })
  },

  toggleReady: () => {
    networkClient.toggleReady()
  },

  startGame: () => {
    networkClient.startGame()
  },

  changeRole: (role) => {
    networkClient.changeRole(role)
    set({ selectedRole: role })
  },

  refreshRooms: async () => {
    try {
      const rooms = await networkClient.getAvailableRooms()
      set({
        availableRooms: rooms.map(r => ({
          roomId: r.roomId,
          name: r.metadata?.roomName || `Pokoj ${r.roomId.slice(0, 4)}`,
          players: r.clients,
          maxPlayers: r.maxClients,
        }))
      })
    } catch (error) {
      console.error('Failed to refresh rooms:', error)
    }
  },

  requestPuzzle: (difficulty = 'easy') => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_puzzle', { difficulty })
    }
  },

  closePuzzle: () => {
    set({ activePuzzle: null })
  },

  syncFromRoom: (room) => {
    set({
      currentRoomId: room.roomId,
      mySessionId: room.sessionId,
    })

    // Helper to convert player data
    const toPlayer = (player: any, sessionId: string): Player => ({
      sessionId,
      nickname: player.nickname || `Gracz${sessionId.slice(0, 4)}`,
      role: player.role as PlayerRole,
      x: player.x,
      y: player.y,
      score: player.score,
      isReady: player.isReady,
      isAI: player.isAI,
    })

    // Initial sync of existing players
    const initialPlayers: Record<string, Player> = {}
    room.state.players.forEach((player: any, sessionId: string) => {
      initialPlayers[sessionId] = toPlayer(player, sessionId)
    })
    set({ players: initialPlayers })

    // Sync new players
    room.state.players.onAdd((player: any, sessionId: string) => {
      const players = { ...get().players }
      players[sessionId] = toPlayer(player, sessionId)
      set({ players })

      // Track changes
      player.onChange(() => {
        const players = { ...get().players }
        if (players[sessionId]) {
          players[sessionId] = toPlayer(player, sessionId)
          set({ players })
        }
      })
    })

    room.state.players.onRemove((_: any, sessionId: string) => {
      const players = { ...get().players }
      delete players[sessionId]
      set({ players })
    })

    // Sync game state
    room.state.onChange(() => {
      set({
        phase: room.state.phase as any,
        timer: room.state.timer,
        stability: room.state.stability,
        squad: room.state.squad as any,
      })
    })

    // Handle messages
    room.onMessage('game_started', () => {
      set({ screen: 'game', phase: 'explore' })
    })

    room.onMessage('role_assigned', (data: { role: string }) => {
      set({ selectedRole: data.role as PlayerRole })
    })

    room.onMessage('error', (data: { message: string }) => {
      set({ error: data.message })
    })

    room.onMessage('players_updated', () => {
      // Force refresh players from room state
      const players: Record<string, Player> = {}
      room.state.players.forEach((player: any, sessionId: string) => {
        players[sessionId] = toPlayer(player, sessionId)
      })
      set({ players })
    })

    room.onMessage('puzzle_started', (data: { puzzleId: string; type: string; difficulty: string; sequence: string[] }) => {
      set({
        activePuzzle: {
          puzzleId: data.puzzleId,
          type: data.type,
          difficulty: data.difficulty,
          sequence: data.sequence,
        },
        phase: 'puzzle',
      })
    })

    room.onMessage('puzzle_solved', () => {
      set({ activePuzzle: null, phase: 'explore' })
    })
  },
}))
